import { FC, useEffect, useRef } from "react";
import { View } from "react-native";
import Svg, {
  Defs,
  FeBlend,
  FeColorMatrix,
  FeGaussianBlur,
  Filter,
} from "react-native-svg";

interface AnimatedBackgroundProps {
  className?: string;
}

const AnimatedBackground: FC<AnimatedBackgroundProps> = ({
  className = "",
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let nodes: {
      r: number;
      vx: number;
      vy: number;
      x: number;
      y: number;
    }[] = [];

    function resize() {
      if (!canvas || !container || !ctx) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cssWidth = container.clientWidth;
      const cssHeight = container.clientHeight || 420;

      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      canvas.width = Math.floor(cssWidth * dpr);
      canvas.height = Math.floor(cssHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      width = cssWidth;
      height = cssHeight;
      nodes = createNodes();
    }

    function createNodes() {
      const area = width * height;
      const count = Math.max(12, Math.floor(area / 45000));
      const margin = Math.min(width, height) * 0.1;
      const newNodes: {
        r: number;
        vx: number;
        vy: number;
        x: number;
        y: number;
      }[] = [];

      for (let i = 0; i < count; i++) {
        const biasToEdge = Math.random() < 0.8;
        let x = Math.random() * width;
        let y = Math.random() * height;

        if (biasToEdge) {
          const edge = Math.floor(Math.random() * 4);
          if (edge === 0) {
            // top
            x = Math.random() * width;
            y = Math.random() * margin;
          } else if (edge === 1) {
            // bottom
            x = Math.random() * width;
            y = height - Math.random() * margin;
          } else if (edge === 2) {
            // left
            x = Math.random() * margin;
            y = Math.random() * height;
          } else {
            // right
            x = width - Math.random() * margin;
            y = Math.random() * height;
          }
        }

        newNodes.push({
          r: 2 + Math.random() * 1.5,
          vx: (Math.random() - 0.5) * 0.25,
          vy: (Math.random() - 0.5) * 0.25,
          x,
          y,
        });
      }

      return newNodes;
    }

    function step() {
      if (!ctx) return;
      ctx.clearRect(0, 0, width, height);

      // Draw connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;

          if (d2 < 130 * 130) {
            const alpha = 1 - d2 / (130 * 130);
            const rgb =
              typeof document !== "undefined"
                ? getComputedStyle(document.documentElement)
                    .getPropertyValue("--color-primary-500")
                    .trim()
                    .replace(/\s+/g, ",") || "139,92,246"
                : "139,92,246";
            ctx.strokeStyle = `rgba(${rgb},${alpha * 0.25})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Draw and update nodes
      for (const n of nodes) {
        ctx.fillStyle = "rgba(0,0,0,0.9)";
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();

        n.x += n.vx;
        n.y += n.vy;

        if (n.x < 0 || n.x > width) n.vx *= -1;
        if (n.y < 0 || n.y > height) n.vy *= -1;
      }

      requestAnimationFrame(step);
    }

    resize();
    step();

    const ro = new ResizeObserver(resize);
    ro.observe(container);

    return () => ro.disconnect();
  }, []);

  return (
    <View className={`absolute inset-0 overflow-hidden ${className}`}>
      {/* Gradient background */}
      <div aria-hidden className="absolute inset-0 gradient-bg" />

      {/* Background agent network canvas */}
      <div
        ref={containerRef}
        className="absolute inset-0 z-0 pointer-events-none"
      >
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full block"
        />
      </div>

      {/* Gooey background blobs */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{ filter: "url(#goo)" }}
      >
        <div className="absolute -top-24 -left-16 w-[34rem] h-[34rem] rounded-full bg-black/20 blur-3xl" />
        <div className="absolute top-1/3 -right-24 w-[36rem] h-[36rem] rounded-full bg-black/15 blur-3xl" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[30rem] h-[14rem] rounded-full bg-black/10 blur-2xl" />
      </div>

      {/* SVG goo filter */}
      <Svg aria-hidden className="absolute -z-10" height="0" width="0">
        <Defs>
          <Filter height="100%" id="goo" width="100%" x="0%" y="0%">
            <FeGaussianBlur
              in="SourceGraphic"
              result="blur"
              stdDeviation="12"
            />
            <FeColorMatrix
              in="blur"
              result="goo"
              type="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 24 -12"
            />
            <FeBlend in="SourceGraphic" in2="goo" />
          </Filter>
        </Defs>
      </Svg>

      {/* Soft grid */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 soft-grid opacity-20"
      />

      {/* Vignette */}
      <div aria-hidden className="absolute inset-0 -z-10 vignette" />
    </View>
  );
};

export default AnimatedBackground;
